import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import type { ComparisonDatum } from '../reportData';
import { series, chrome, chartFont } from '../reportTheme';
import ChartTooltip from './ChartTooltip';

const axisTick = { fill: chrome.muted, fontSize: 12, fontFamily: chartFont };

interface ComparisonBarChartProps {
  data: ComparisonDatum[];
  unit?: string;
  max?: number;
  /** Series names; default to plan/actual. */
  plannedLabel?: string;
  actualLabel?: string;
}

/**
 * Two bars per category - by default plan against actual (the spec's
 * "Soll-/Ist-Vergleich"), but the series can be named for any pair, e.g.
 * progress against quality-assured progress. A category whose second value is
 * not computable shows only its first bar, rather than a second one at zero.
 */
const ComparisonBarChart: React.FC<ComparisonBarChartProps> = ({
  data,
  unit = '',
  max,
  plannedLabel,
  actualLabel,
}) => {
  const { t } = useTranslation('templates');
  const firstLabel = plannedLabel ?? t('report.plannedVsActual.planned');
  const secondLabel = actualLabel ?? t('report.plannedVsActual.actual');

  const rows = data.map((datum) => ({
    label: datum.label,
    [firstLabel]: datum.planned,
    [secondLabel]: datum.actual,
  }));

  const fmt = (value: number | string) => `${value}${unit}`;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={rows}
        layout="vertical"
        margin={{ top: 4, right: 40, left: 8, bottom: 0 }}
        barCategoryGap="24%"
      >
        <CartesianGrid stroke={chrome.grid} horizontal={false} />
        <XAxis
          type="number"
          domain={max != null ? [0, max] : ['auto', 'auto']}
          tick={axisTick}
          tickLine={false}
          axisLine={false}
          tickFormatter={fmt}
        />
        <YAxis
          type="category"
          dataKey="label"
          tick={axisTick}
          tickLine={false}
          axisLine={{ stroke: chrome.axis }}
          width={120}
        />
        <Tooltip content={<ChartTooltip valueFormatter={fmt} />} cursor={{ fill: chrome.grid }} />
        <Legend
          iconType="circle"
          iconSize={8}
          wrapperStyle={{ fontFamily: chartFont, fontSize: 12, color: chrome.textSecondary }}
        />
        <Bar dataKey={firstLabel} fill={chrome.muted} radius={[0, 4, 4, 0]} barSize={10} />
        <Bar dataKey={secondLabel} fill={series.blue} radius={[0, 4, 4, 0]} barSize={10} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default ComparisonBarChart;
